import React from "react";
import styled from "styled-components";
import Markdown from "src/components/Markdown";
import { applyNoWrapMacro } from "src/utils/nowrap";

const ACCENT = "#7ed4cc";

const ASSET_BASE = process.env.REACT_APP_PGFE_IMG_ASSETS_PATH || "";

const ParadiseGiveawayIntro = ({
  headline,
  subheadline,
  intro,
  dateParts = [],
  ticketTitle,
  ticketText,
  revealTitle,
  revealText,
  prizes = [],
  packageTitle,
  packageItems = [],
  palmImage,
  howTitle,
  howText,
}) => {
  return (
    <Wrap>
      <Teal>
        <Inner>
          {headline && (
            <Headline
              dangerouslySetInnerHTML={{ __html: applyNoWrapMacro(headline) }}
            />
          )}
          {subheadline && <Subhead>{subheadline}</Subhead>}
          {intro && (
            <Intro>
              <Markdown>{intro}</Markdown>
            </Intro>
          )}
          {dateParts.length > 0 && (
            <Pill>
              {dateParts.map((part, index) => {
                const item = typeof part === "string" ? { text: part } : part;
                const separator = index === 0 ? null : index === 1 ? "|" : "•";
                return (
                  <DateGroup key={`${item.text}-${index}`}>
                    {separator && <Sep aria-hidden="true">{separator}</Sep>}
                    <DatePart $accent={Boolean(item.accent)}>{item.text}</DatePart>
                  </DateGroup>
                );
              })}
            </Pill>
          )}
          <Columns>
            <Copy>
              {ticketTitle && <GoldTitle>{ticketTitle}</GoldTitle>}
              {ticketText && (
                <CopyText>
                  <Markdown>{ticketText}</Markdown>
                </CopyText>
              )}
              {ticketText && (revealTitle || revealText) && (
                <GoldRule aria-hidden="true" />
              )}
              {revealTitle && <GoldTitle>{revealTitle}</GoldTitle>}
              {revealText && (
                <CopyText>
                  <Markdown>{revealText}</Markdown>
                </CopyText>
              )}
              {prizes.length > 0 && (
                <PrizeRow>
                  {prizes.map((prize, index) => (
                    <React.Fragment key={prize.count + prize.label}>
                      {index > 0 && <PrizeDivider aria-hidden="true" />}
                      <Prize>
                        <PrizeText>
                          {(() => {
                            const lines = prize.label
                              .split("\n")
                              .map((line) => line.trim())
                              .filter(Boolean);
                            const firstBeside = lines[0] && !/\s/.test(lines[0]);
                            const rest = lines.slice(firstBeside ? 1 : 0);
                            return (
                              <>
                                <Count>
                                  {prize.count} x{firstBeside ? " " : ""}
                                </Count>
                                {firstBeside ? lines[0] : null}
                                {rest.map((line) => (
                                  <PrizeLine key={line}>{line}</PrizeLine>
                                ))}
                              </>
                            );
                          })()}
                        </PrizeText>
                        {prize.note && <PrizeNote>{prize.note}</PrizeNote>}
                      </Prize>
                    </React.Fragment>
                  ))}
                </PrizeRow>
              )}
            </Copy>
            <Card>
              {palmImage && (
                <PalmArt src={`${ASSET_BASE}${palmImage}`} alt="" />
              )}
              {packageTitle && <CardTitle>{packageTitle}</CardTitle>}
              <CardList>
                {packageItems.map((item) => (
                  <CardItem key={item.text}>
                    {item.icon && (
                      <IconBadge>
                        <Icon
                          src={`${ASSET_BASE}${item.icon}`}
                          alt=""
                        />
                      </IconBadge>
                    )}
                    <span>{item.text}</span>
                  </CardItem>
                ))}
              </CardList>
            </Card>
          </Columns>
        </Inner>
      </Teal>
      <How>
        <Inner>
          {howTitle && <HowTitle>{howTitle}</HowTitle>}
          {howText && (
            <HowCopy>
              <Markdown>{howText}</Markdown>
            </HowCopy>
          )}
        </Inner>
      </How>
    </Wrap>
  );
};

const Wrap = styled.div`
  width: 100%;
  color: #fff;
`;

const Inner = styled.div`
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 24px;
`;

const Teal = styled.section`
  background: #14586a;
  padding: 56px 0 48px;
  text-align: center;
`;

const Headline = styled.h1`
  max-width: 920px;
  margin: 0 auto;
  color: #fff;
  font-size: clamp(30px, 3.6vw, 44px);
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: 0;
  text-wrap: balance;

  .nowrap {
    white-space: nowrap;
  }

  &::after {
    content: "";
    display: block;
    width: 100%;
    max-width: 275px;
    height: 2px;
    margin: 12px auto 0;
    background: var(--mustard);
  }
`;

const Subhead = styled.p`
  && {
    margin: 18px 0 0;
  }
  color: #fff;
  font-size: clamp(18px, 2vw, 22px);
  font-weight: 500;
  line-height: 1.3;
`;

const Intro = styled.div`
  max-width: 720px;
  margin: 18px auto 0;
  font-size: 17px;
  line-height: 1.45;

  && p {
    margin: 0 0 12px;
    font-size: 17px;
    line-height: 1.45;
  }

  p:last-child {
    margin-bottom: 0;
  }

  .accent {
    color: ${ACCENT};
    font-weight: 700;
  }
`;

const Pill = styled.div`
  display: flex;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin: 32px auto 0;
  padding: 18px 32px;
  border-radius: 24px;
  background: #1a1c1e;
  color: #fff;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 0.03em;
  line-height: 1.2;

  @media (max-width: 720px) {
    flex-wrap: wrap;
    row-gap: 8px;
    padding: 16px 18px;
    font-size: 16px;
  }
`;

const DateGroup = styled.span`
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
`;

const DatePart = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== "$accent",
})`
  color: ${(props) => (props.$accent ? ACCENT : "#fff")};
`;

const Sep = styled.span`
  color: #fff;
  margin: 0 22px;
  font-weight: 500;

  @media (max-width: 720px) {
    margin: 0 12px;
  }
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(280px, 0.85fr);
  gap: 36px;
  align-items: center;
  margin-top: 40px;
  text-align: left;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 28px;
  }
`;

const Copy = styled.div`
  color: #fff;

  hr + h2 {
    margin-top: 0;
  }
`;

const GoldTitle = styled.h2`
  margin: 22px 0 8px;
  color: ${ACCENT};
  font-size: clamp(24px, 2.4vw, 30px);
  font-weight: 800;
  line-height: 1.2;

  &:first-child {
    margin-top: 0;
  }
`;

const GoldRule = styled.hr`
  border: 0;
  height: 2px;
  margin: 18px 0 16px;
  background: var(--mustard);
`;

const CopyText = styled.div`
  font-size: 17px;
  line-height: 1.55;

  p {
    margin: 0 0 10px;
  }

  p:last-child {
    margin-bottom: 0;
  }
`;

const PrizeRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: stretch;
  column-gap: 28px;
  margin-top: 22px;
  width: 100%;

  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr);
    row-gap: 16px;
  }
`;

const PrizeDivider = styled.span`
  width: 2px;
  background: #d0a05c;
  justify-self: center;

  @media (max-width: 600px) {
    width: 100%;
    height: 2px;
  }
`;

const Prize = styled.div`
  min-width: 0;
`;

const PrizeText = styled.p`
  margin: 0;
  color: #d0a05c;
  font-size: clamp(22px, 2.15vw, 30px);
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: 0.01em;
  text-transform: uppercase;
  text-align: left;
`;

const Count = styled.span`
  color: #fff;
  letter-spacing: -0.5px;
`;

const PrizeLine = styled.span`
  display: block;
`;

const PrizeNote = styled.p`
  margin: 10px 0 0;
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.3;
`;

const Card = styled.aside`
  position: relative;
  overflow: hidden;
  background: #fff;
  color: #1a1a1a;
  border: 2px solid var(--mustard);
  border-radius: 18px;
  padding: 28px 26px 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
`;

const PalmArt = styled.img`
  position: absolute;
  right: -4%;
  bottom: -8%;
  width: 78%;
  height: auto;
  object-fit: contain;
  object-position: right bottom;
  opacity: 0.55;
  pointer-events: none;
  user-select: none;
`;

const CardTitle = styled.h2`
  position: relative;
  z-index: 1;
  margin: 0 0 18px;
  font-size: 22px;
  font-weight: 800;
  line-height: 1.25;
  color: #1a1a1a;
  text-align: center;
`;

const CardList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const CardItem = styled.li`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
  font-size: 16px;
  line-height: 1.35;
  font-weight: 600;
`;

const IconBadge = styled.span`
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #16323c;
`;

const Icon = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
  filter: brightness(0) invert(1);
`;

const How = styled.section`
  background: #fff;
  color: #161616;
  text-align: center;
  padding: 56px 0 48px;
`;

const HowTitle = styled.h2`
  margin: 0 0 22px;
  color: #161616;
  font-size: clamp(32px, 4vw, 44px);
  font-weight: 800;
  line-height: 1.05;

  &::after {
    content: "";
    display: block;
    width: 100%;
    max-width: 275px;
    height: 2px;
    margin: 12px auto 0;
    background: var(--mustard);
  }
`;

const HowCopy = styled.div`
  max-width: 1040px;
  margin: 0 auto;
  font-size: 18px;
  line-height: 1.45;

  .nowrap {
    white-space: nowrap;
  }

  @media (max-width: 900px) {
    .nowrap {
      white-space: normal;
    }
  }

  && p {
    margin: 0 0 14px;
    font-size: 18px;
    line-height: 1.45;
  }

  p:last-child {
    margin-bottom: 0;
  }
`;

export default ParadiseGiveawayIntro;
